import React, { useState, useEffect } from 'react';

const LoadingScreen = () => {
  const [progress, setProgress] = useState(0);
  const [isComplete, setIsComplete] = useState(false);
  const [themeLoaded, setThemeLoaded] = useState(false);

  // Load theme preferences from localStorage
  useEffect(() => {
    const loadTheme = () => {
      const savedTheme = localStorage.getItem('portfolio-theme');
      const savedColorScheme = localStorage.getItem('portfolio-color-scheme');
      const savedCustomColor = localStorage.getItem('portfolio-custom-color');
      const savedLightBg = localStorage.getItem('portfolio-light-bg');
      const savedDarkBg = localStorage.getItem('portfolio-dark-bg');
      const savedTitleColor = localStorage.getItem('portfolio-title-color');
      const savedBodyColor = localStorage.getItem('portfolio-body-color');
      
      const root = document.documentElement;
      
      // Apply theme mode
      if (savedTheme === 'dark') {
        root.classList.add('dark');
      } else {
        root.classList.remove('dark');
      }
      
      // Apply color scheme
      if (savedColorScheme) {
        const colorSchemes = {
          gold: { primary: '#D4AF37', primaryHover: '#B8941F' },
          blue: { primary: '#3B82F6', primaryHover: '#2563EB' },
          purple: { primary: '#8B5CF6', primaryHover: '#7C3AED' },
          green: { primary: '#10B981', primaryHover: '#059669' },
          red: { primary: '#EF4444', primaryHover: '#DC2626' }
        };
        
        const scheme = colorSchemes[savedColorScheme] || colorSchemes.gold;
        root.style.setProperty('--color-primary', scheme.primary);
        root.style.setProperty('--color-primary-hover', scheme.primaryHover);
        root.style.setProperty('--gradient-primary', `linear-gradient(to right, ${scheme.primary}, ${scheme.primaryHover})`);
      }
      
      // Apply custom color if exists
      if (savedCustomColor) {
        root.style.setProperty('--color-primary', savedCustomColor);
        root.style.setProperty('--gradient-primary', `linear-gradient(to right, ${savedCustomColor}, ${savedCustomColor})`);
      }
      
      // Apply background colors
      root.style.setProperty('--bg-light', savedLightBg || '#ffffff');
      root.style.setProperty('--bg-dark', savedDarkBg || '#000000');
      root.style.setProperty('--text-title', savedTitleColor || '#ffffff');
      root.style.setProperty('--text-body', savedBodyColor || '#cccccc');
      
      setThemeLoaded(true);
    };
    
    loadTheme();
  }, []);

  useEffect(() => {
    if (!themeLoaded) return;
    
    const interval = setInterval(() => {
      setProgress(prev => {
        const increment = Math.random() * 15;
        const newProgress = prev + increment;
        
        if (newProgress >= 100) {
          clearInterval(interval);
          setTimeout(() => setIsComplete(true), 500);
          return 100;
        }
        return newProgress;
      });
    }, 100);

    return () => clearInterval(interval);
  }, [themeLoaded]);

  if (isComplete) return null;

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ backgroundColor: 'var(--bg-dark)' }}
    >
      <div className="text-center">
        {/* Logo/Name */}
        <div className="mb-8">
          <h1 
            className="text-4xl font-bold mb-2"
            style={{ color: 'var(--text-title)' }}
          >
            ASHRAF ALI
          </h1>
          <p 
            className="text-lg"
            style={{ color: 'var(--text-body)' }}
          >
            Software Development & Machine Learning Engineer
          </p>
        </div>

        {/* Loading Animation */}
        <div className="mb-8">
          <div 
            className="w-16 h-16 border-4 border-gray-700 rounded-full animate-spin mx-auto"
            style={{ borderTopColor: 'var(--color-primary)' }}
          ></div>
        </div>

        {/* Progress Bar */}
        <div className="w-64 mx-auto">
          <div className="flex justify-between text-sm mb-2">
            <span style={{ color: 'var(--text-body)' }}>Loading Portfolio</span>
            <span style={{ color: 'var(--text-body)' }}>{Math.round(progress)}%</span>
          </div>
          <div className="w-full bg-gray-700 rounded-full h-2">
            <div 
              className="accent-gradient h-2 rounded-full transition-all duration-300 ease-out"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* Loading Text */}
        <div className="mt-6 text-gray-400 text-sm">
          {progress < 30 && "Initializing components..."}
          {progress >= 30 && progress < 60 && "Loading animations..."}
          {progress >= 60 && progress < 90 && "Preparing content..."}
          {progress >= 90 && "Almost ready..."}
        </div>
      </div>
    </div>
  );
};

export default LoadingScreen;
